import type { HsHypersceneTypes } from '../types/hyperscene-types.type';

export class HsMatrix3 {
  public readonly values: HsHypersceneTypes.Array9;

  constructor(
    a00: number,
    a01: number,
    a02: number,
    a10: number,
    a11: number,
    a12: number,
    a20: number,
    a21: number,
    a22: number
  ) {
    this.values = [a00, a01, a02, a10, a11, a12, a20, a21, a22];
  }

  /**
   * Sets set.
   * @returns Result value.
   */
  public set(
    a00: number,
    a01: number,
    a02: number,
    a10: number,
    a11: number,
    a12: number,
    a20: number,
    a21: number,
    a22: number
  ): HsMatrix3 {
    this.values[0] = a00;
    this.values[1] = a01;
    this.values[2] = a02;
    this.values[3] = a10;
    this.values[4] = a11;
    this.values[5] = a12;
    this.values[6] = a20;
    this.values[7] = a21;
    this.values[8] = a22;
    return this;
  }

  /**
   * Copies copy.
   * @param other other.
   * @returns Result value.
   */
  public copy(other: HsMatrix3): HsMatrix3 {
    this.values[0] = other.values[0];
    this.values[1] = other.values[1];
    this.values[2] = other.values[2];
    this.values[3] = other.values[3];
    this.values[4] = other.values[4];
    this.values[5] = other.values[5];
    this.values[6] = other.values[6];
    this.values[7] = other.values[7];
    this.values[8] = other.values[8];
    return this;
  }

  /**
   * Sets from array.
   * @param array array.
   * @returns Result value.
   */
  public setFromArray(array: HsHypersceneTypes.Array9): HsMatrix3 {
    this.values[0] = array[0];
    this.values[1] = array[1];
    this.values[2] = array[2];
    this.values[3] = array[3];
    this.values[4] = array[4];
    this.values[5] = array[5];
    this.values[6] = array[6];
    this.values[7] = array[7];
    this.values[8] = array[8];
    return this;
  }

  /**
   * Sets from matrix4.
   * @param matrix4Values matrix4 values.
   * @returns Result value.
   */
  public setFromMatrix4(matrix4Values: HsHypersceneTypes.Array16): HsMatrix3 {
    this.values[0] = matrix4Values[0];
    this.values[1] = matrix4Values[4];
    this.values[2] = matrix4Values[8];
    this.values[3] = matrix4Values[1];
    this.values[4] = matrix4Values[5];
    this.values[5] = matrix4Values[9];
    this.values[6] = matrix4Values[2];
    this.values[7] = matrix4Values[6];
    this.values[8] = matrix4Values[10];
    return this;
  }

  /**
   * Handles multiply.
   * @param other other.
   * @returns Result value.
   */
  public multiply(other: HsMatrix3): HsMatrix3 {
    return this.multiplyMatrices(this, other);
  }

  /**
   * Handles multiply matrices.
   * @param first first.
   * @param second second.
   * @returns Result value.
   */
  public multiplyMatrices(first: HsMatrix3, second: HsMatrix3): HsMatrix3 {
    const a00: number = first.values[0];
    const a01: number = first.values[1];
    const a02: number = first.values[2];
    const a10: number = first.values[3];
    const a11: number = first.values[4];
    const a12: number = first.values[5];
    const a20: number = first.values[6];
    const a21: number = first.values[7];
    const a22: number = first.values[8];
    const b00: number = second.values[0];
    const b01: number = second.values[1];
    const b02: number = second.values[2];
    const b10: number = second.values[3];
    const b11: number = second.values[4];
    const b12: number = second.values[5];
    const b20: number = second.values[6];
    const b21: number = second.values[7];
    const b22: number = second.values[8];
    this.values[0] = b00 * a00 + b01 * a10 + b02 * a20;
    this.values[1] = b00 * a01 + b01 * a11 + b02 * a21;
    this.values[2] = b00 * a02 + b01 * a12 + b02 * a22;
    this.values[3] = b10 * a00 + b11 * a10 + b12 * a20;
    this.values[4] = b10 * a01 + b11 * a11 + b12 * a21;
    this.values[5] = b10 * a02 + b11 * a12 + b12 * a22;
    this.values[6] = b20 * a00 + b21 * a10 + b22 * a20;
    this.values[7] = b20 * a01 + b21 * a11 + b22 * a21;
    this.values[8] = b20 * a02 + b21 * a12 + b22 * a22;
    return this;
  }

  /**
   * Handles inverse.
   * @returns Result value.
   */
  public inverse(): HsMatrix3 {
    const a00: number = this.values[0];
    const a10: number = this.values[1];
    const a20: number = this.values[2];
    const a01: number = this.values[3];
    const a11: number = this.values[4];
    const a21: number = this.values[5];
    const a02: number = this.values[6];
    const a12: number = this.values[7];
    const a22: number = this.values[8];
    const t11: number = a22 * a11 - a21 * a12;
    const t12: number = a21 * a02 - a22 * a01;
    const t13: number = a12 * a01 - a11 * a02;
    const det: number = a00 * t11 + a10 * t12 + a20 * t13;
    if (det === 0) {
      this.set(0, 0, 0, 0, 0, 0, 0, 0, 0);
      return this;
    }
    const detInv: number = 1 / det;
    this.values[0] = t11 * detInv;
    this.values[1] = (a20 * a12 - a22 * a10) * detInv;
    this.values[2] = (a21 * a10 - a20 * a11) * detInv;
    this.values[3] = t12 * detInv;
    this.values[4] = (a22 * a00 - a20 * a02) * detInv;
    this.values[5] = (a20 * a01 - a21 * a00) * detInv;
    this.values[6] = t13 * detInv;
    this.values[7] = (a10 * a02 - a12 * a00) * detInv;
    this.values[8] = (a11 * a00 - a10 * a01) * detInv;
    return this;
  }

  /**
   * Handles transpose.
   * @returns Result value.
   */
  public transpose(): HsMatrix3 {
    let tmp: number = this.values[1];
    this.values[1] = this.values[3];
    this.values[3] = tmp;
    tmp = this.values[2];
    this.values[2] = this.values[6];
    this.values[6] = tmp;
    tmp = this.values[5];
    this.values[5] = this.values[7];
    this.values[7] = tmp;
    return this;
  }

  /**
   * Builds identity matrix.
   * @returns Result value.
   */
  public static makeIdentityMatrix(): HsMatrix3 {
    return new HsMatrix3(1, 0, 0, 0, 1, 0, 0, 0, 1);
  }

  /**
   * Builds copy.
   * @returns Result value.
   */
  public static makeCopy(other: HsMatrix3): HsMatrix3 {
    return new HsMatrix3(...other.values);
  }
}
